/**
 * What state an agent's X.509-SVID is in — one classification, three readers.
 *
 * <h3>Why it moved out of the panel</h3>
 *
 * `SpiffeIdentityPanel` held this privately, with a comment saying its
 * thresholds were kept aligned with the full identity page "to avoid the
 * legacy panel and the full identity page disagreeing on a single row in the
 * field". Protect then became a third reader of the same two fields and
 * collapsed them to active-or-not — so a certificate that expired twenty
 * minutes ago was reported to a tenant as Active. That is exactly the
 * disagreement the comment was guarding against, arriving through a door
 * nobody was watching. Alignment maintained by hand is not alignment.
 *
 * <h3>What is shared, and what is not</h3>
 *
 * The buckets and their words. Not the styling: the panel wears the v2 design
 * system and Protect wears its own tokens, and a shared stylesheet would be a
 * second coupling earning nothing.
 */

export const SVID_STATUS = {
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
  NOT_PROVISIONED: 'not_provisioned',
};

/** How close to expiry counts as expiring. */
export const SVID_EXPIRING_WINDOW_MS = 4 * 3_600_000;

/**
 * Classify from the expiry the agent row carries.
 *
 * <h3>Why `svidActive` is not the gate</h3>
 *
 * It is not a stored fact. `Agent.isSvidActive()` returns
 * `svidExpiresAt != null && svidExpiresAt.isAfter(now)` — so it says "not
 * expired", never "one was issued". Gating on it sent every lapsed
 * certificate to NOT_PROVISIONED and left the EXPIRED bucket unreachable in
 * production: an agent whose certificate ran out an hour ago was reported as
 * one that never had a certificate at all. Those are different facts, and the
 * second means the gateway is refusing its calls right now.
 *
 * So the expiry decides, which is the same comparison the flag was doing, done
 * once and in the open. A row with no expiry has nothing to have expired, and
 * an unreadable one is not guessed at.
 */
export function classifySvidStatus({ svidExpiresAt } = {}) {
  if (!svidExpiresAt) return SVID_STATUS.NOT_PROVISIONED;
  const ms = new Date(svidExpiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return SVID_STATUS.NOT_PROVISIONED;
  if (ms <= 0) return SVID_STATUS.EXPIRED;
  if (ms < SVID_EXPIRING_WINDOW_MS) return SVID_STATUS.EXPIRING;
  return SVID_STATUS.ACTIVE;
}

const LABELS = {
  [SVID_STATUS.ACTIVE]: 'active',
  [SVID_STATUS.EXPIRING]: 'expiring soon',
  [SVID_STATUS.EXPIRED]: 'expired',
  [SVID_STATUS.NOT_PROVISIONED]: 'not provisioned',
};

/** The state in the platform's own words. */
export const svidStatusLabel = (status) =>
  LABELS[status] || LABELS[SVID_STATUS.NOT_PROVISIONED];
